import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ToggleGroup } from 'components/_common/ToggleGroup';
import { OverviewViewMode, UserSystemGroup } from 'store/storeTypes';
import { isDtfOnlyUser } from 'reducers/ui/timeViewModeUiReducer';
import { mobileStyles } from './MobileSettings.styles';

interface MobilePreferencesProps {
  userSettings: any;
  updatedSettings: any;
  isProductionHoursConfigured: boolean;
  handleChangeDefaultValue: (event: React.MouseEvent<HTMLElement>, newVal: any) => void;
  handleChangeViewMode: (event: React.MouseEvent<HTMLElement>, newVal: any) => void;
}

const MobilePreferences: React.FC<MobilePreferencesProps> = ({
  userSettings,
  updatedSettings,
  isProductionHoursConfigured,
  handleChangeDefaultValue,
  handleChangeViewMode,
}) => {
  const { t } = useTranslation();

  const groups = userSettings.data.userSystemGroup?.map(group => {
    return {
      value: UserSystemGroup[group],
      label: group,
    };
  });

  const viewModes = [
    { value: OverviewViewMode.TwentyFourHours, label: t('settings.twentyFourHours') },
    {
      value: OverviewViewMode.Production,
      label: t('settings.production'),
      disabled: !isProductionHoursConfigured,
      tooltip: isProductionHoursConfigured ? undefined : t('settings.productionHoursNotSet'),
    },
  ];

  const hasMultipleSystemGroups = userSettings.data.userSystemGroup?.length >= 2;
  const isViewModeHidden = isDtfOnlyUser(userSettings.data.userSystemGroup);

  return (
    <Box sx={mobileStyles.section}>
      <Box sx={mobileStyles.sectionTitleWrapper}>
        <Typography variant="h3" sx={mobileStyles.sectionTitle}>
          {t('settings.preferences')}
        </Typography>
      </Box>
      <Box sx={mobileStyles.sectionList}>
        {hasMultipleSystemGroups && (
          <Box sx={mobileStyles.sectionListItem}>
            <Box sx={mobileStyles.sectionListItemWrapper}>
              <Box sx={mobileStyles.sectionListItemLeftPart}>
                <Typography sx={mobileStyles.sectionListItemTitle}>
                  {t('settings.defaultSystemType')}
                </Typography>
              </Box>
              <Box sx={mobileStyles.sectionListItemRightPart}>
                <ToggleGroup
                  value={
                    updatedSettings && updatedSettings.hasOwnProperty('defaultSystemGroup')
                      ? updatedSettings.defaultSystemGroup
                      : userSettings.data?.defaultSystemGroup
                  }
                  onChange={handleChangeDefaultValue}
                  name={'defaultSystemGroup'}
                  data={groups}
                />
              </Box>
            </Box>
          </Box>
        )}
        {!isViewModeHidden && (
          <Box sx={mobileStyles.sectionListItem}>
            <Box sx={mobileStyles.sectionListItemWrapper}>
              <Box sx={mobileStyles.sectionListItemLeftPart}>
                <Typography sx={mobileStyles.sectionListItemTitle}>
                  {t('settings.defaultHoursView')}
                </Typography>
              </Box>
              <Box sx={mobileStyles.sectionListItemRightPart}>
                <ToggleGroup
                  value={
                    updatedSettings && updatedSettings.hasOwnProperty('defaultShiftMode')
                      ? updatedSettings.defaultShiftMode
                      : userSettings.data?.defaultShiftMode || OverviewViewMode.TwentyFourHours
                  }
                  onChange={handleChangeViewMode}
                  name={'defaultShiftMode'}
                  data={viewModes}
                  width={180}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MobilePreferences;
